import { useState, useEffect, useCallback } from 'react';
import * as userAPI from '../models/userAPI';

export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    return userAPI.getAllUsers()
      .then(response => {
        setUsers(response.data);
        setLoading(false);
        return response.data;
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
        throw err;
      });
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return { users, loading, error, refetch: fetchUsers };
};


export const useCreateUser = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async (data) => {
    setIsSubmitting(true);
    setError(null); // Clear any previous errors
    
    try {
      const response = await userAPI.createUser(data);
      setIsSubmitting(false);
      
      // Handle both success scenarios - 200 and 201 are both success statuses
      if (response.status === 200 || response.status === 201) {
        return response.data;
      } else {
        // Unexpected success status
        setError(`Unexpected response status: ${response.status}`);
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (err) {
      // Check if this is an axios error with a response
      if (err.response) {
        // Server responded with a status code outside the 2xx range
        const errorMessage = err.response.data?.message || `Error: ${err.response.status}`;
        setError(errorMessage);
        throw new Error(errorMessage);
      } else if (err.request) {
        // Request was made but no response received
        const errorMessage = 'No response received from server';
        setError(errorMessage);
        throw new Error(errorMessage);
      } else {
        // Error in setting up the request
        setError(err.message);
        throw err;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return { handleCreate, isSubmitting, error };
};

export const useUpdateUser = () => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);
  
    const handleUpdate = async (id, data) => {
      setIsUpdating(true);
      try {
        const response = await userAPI.updateUser(id, data);
        setIsUpdating(false);
        return response.data;
      } catch (err) {
        setError(err.message);
        setIsUpdating(false);
        throw err;
      }
    };
  
    return { handleUpdate, isUpdating, error };
  };
  
  export const useDeleteUser = () => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState(null);
  
    const handleDelete = async (id) => {
      setIsDeleting(true);
      try {
        await userAPI.deleteUser(id);
        setIsDeleting(false);
      } catch (err) {
        setError(err.message);
        setIsDeleting(false);
      }
    };
  
    return { handleDelete, isDeleting, error };
  };
  
 
  export const useFetchUserById = (id) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
  
    // Define the fetching logic as a callable function using useCallback
    const fetchUser = useCallback(() => {
      if (id) {
        setLoading(true);
        setError(null); // Reset error state before new fetch attempt
        userAPI.getUserById(id)
          .then(response => {
            setUser(response.data);
            setLoading(false);
          })
          .catch(err => {
            setError(err.message);
            setLoading(false);
          });
      }
    }, [id]);
  
    // Invoke fetchUser when the component mounts and when id changes
    useEffect(() => {
      fetchUser();
    }, [fetchUser]);
  
    // Return all the state management vars and the refetch function
    return { user, loading, error, refetch: fetchUser };
  };
  
  
export const useChangePassword = () => {
  const [isChanging, setIsChanging] = useState(false);
  const [error, setError] = useState(null);

  const handleChangePassword = async (id, passwordData) => {
    setIsChanging(true);
    setError(null);
    
    try {
      const response = await userAPI.changePassword(id, passwordData);
      setIsChanging(false);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      setIsChanging(false);
      throw new Error(errorMessage);
    }
  };

  return { handleChangePassword, isChanging, error };
};